import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Keyboard, Trophy, Palette, Star, Target, TrendingUp, LogOut, Swords, Calendar, Map, Home, Shield, GraduationCap, User, Gamepad2 } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import BlockyCharacter3D from '../components/BlockyCharacter3D';
import NotificationBell from '../components/NotificationBell';
import FamilyProfileSwitcher from '../components/FamilyProfileSwitcher';

const DashboardPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [characterData, setCharacterData] = useState({
    skinTone: '#FFE0BD',
    hairColor: '#4a3728',
    hairStyle: 'short',
    shirtColor: '#4A90D9',
    pantsColor: '#3B82F6',
    shoes: 'trainers',
    eyeStyle: 'normal',
    mouthStyle: 'smile',
    accessory: 'none',
  });

  useEffect(() => {
    fetchStats();
    fetchCharacter();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/typing/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCharacter = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/character`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        setCharacterData({
          skinTone: res.data.character_skin_tone || '#FFE0BD',
          hairColor: res.data.character_hair_color || '#4a3728',
          hairStyle: res.data.character_hair_style || 'short',
          shirtColor: res.data.character_shirt_color || '#4A90D9',
          pantsColor: res.data.character_pants_color || '#3B82F6',
          shoes: res.data.character_shoes || 'trainers',
          eyeStyle: res.data.character_eye_style || 'normal',
          mouthStyle: res.data.character_mouth_style || 'smile',
          accessory: res.data.character_accessory || 'none',
        });
      }
    } catch (e) {
      console.error('Failed to fetch character:', e);
    }
  };

  // Friendly stage names for TypeTown
  const stageNames = ['Newcomer', 'Explorer', 'Builder', 'Master', 'Champion', 'Legend'];
  const stageColors = ['#FF6B6B', '#FFD93D', '#6BCB77', '#4ECDC4', '#A66CFF', '#FF69B4'];
  const stageProgress = user ? ((user.stage - 1) / 5) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-orange-50">
      {/* Friendly decorative clouds */}
      <div className="fixed top-0 left-0 right-0 h-64 bg-gradient-to-b from-sky-200/50 to-transparent pointer-events-none" />
      
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Home className="w-7 h-7 text-coral-orange" />
            <span className="font-display text-2xl font-bold">
              <span className="text-mint-green">Type</span>
              <span className="text-coral-orange">Town</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-end">
            {/* Admin Link - only shown for admins */}
            {(user?.is_admin || user?.email === 'clare@bluecactus.digital') && (
              <Link
                to="/admin"
                className="flex items-center gap-1 sm:gap-2 text-amber-600 hover:text-amber-700 transition-colors"
                data-testid="admin-link"
              >
                <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline font-medium">Admin</span>
              </Link>
            )}
            
            {/* Teacher Dashboard Link */}
            {user?.user_type === 'teacher' && (
              <Link
                to="/teacher/dashboard"
                className="flex items-center gap-1 sm:gap-2 text-sky-blue hover:text-sky-600 transition-colors"
                data-testid="teacher-link"
              >
                <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline font-medium">Class</span>
              </Link>
            )}
            
            {/* Family Profile Switcher */}
            <FamilyProfileSwitcher currentUser={user} onProfileSwitch={refreshUser} />
            
            <div className="flex items-center gap-1 sm:gap-2 bg-sunny-yellow/20 rounded-full px-2 sm:px-4 py-1.5 sm:py-2 border-2 border-sunny-yellow">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-sunny-yellow fill-sunny-yellow" />
              <span className="font-bold text-text-dark text-sm sm:text-base" data-testid="coins-display">
                {user?.credits || 0}
              </span>
              <span className="hidden sm:inline text-xs text-text-medium">coins</span>
            </div>
            <NotificationBell />
            <Link
              to="/profile"
              className="text-text-medium hover:text-mint-green transition-colors p-1.5 sm:p-2 rounded-full hover:bg-mint-green/10"
              data-testid="profile-btn"
            >
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            <button
              onClick={logout}
              className="text-text-medium hover:text-coral-orange transition-colors p-1.5 sm:p-2 rounded-full hover:bg-coral-orange/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8 text-center sm:text-left">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-text-dark mb-2" data-testid="welcome-message">
              Welcome back, <span className="text-mint-green">{user?.username || 'Friend'}</span>! 
              <Sparkles className="w-8 h-8 inline ml-2 text-sunny-yellow" />
            </h1>
            <p className="text-lg text-text-medium">Let's have fun typing today!</p>
          </div>
          
          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Character Card - Spans 2 columns */}
            <div className="md:col-span-2 bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100 flex flex-col items-center" data-testid="pixel-card">
              <div className="relative mb-4">
                <div className="absolute inset-0 rounded-full blur-[60px] opacity-30" style={{ backgroundColor: characterData.shirtColor }} />
                <div className="bg-gradient-to-b from-sky-100 to-mint-green/20 rounded-2xl p-6">
                  <BlockyCharacter3D 
                    size={160} 
                    skinTone={characterData.skinTone}
                    hairColor={characterData.hairColor}
                    hairStyle={characterData.hairStyle}
                    shirtColor={characterData.shirtColor}
                    pantsColor={characterData.pantsColor}
                    shoes={characterData.shoes}
                    eyeStyle={characterData.eyeStyle}
                    mouthStyle={characterData.mouthStyle}
                    accessory={characterData.accessory}
                    isMoving={false}
                    direction="down"
                  />
                </div>
              </div>
              
              <div className="text-center mb-4">
                <div 
                  className="font-display text-2xl font-bold"
                  style={{ color: stageColors[(user?.stage || 1) - 1] }}
                >
                  {stageNames[(user?.stage || 1) - 1]}
                </div>
                <div className="text-sm text-text-medium">Level {user?.stage || 1}</div>
              </div>
              
              {/* Stage Progress */}
              <div className="w-full">
                <div className="flex justify-between text-sm text-text-medium mb-2">
                  <span>Your journey</span>
                  <span className="font-bold">{Math.round(stageProgress)}%</span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${stageProgress}%`,
                      background: `linear-gradient(90deg, ${stageColors[0]}, ${stageColors[(user?.stage || 1) - 1]})`
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-text-medium mt-2">
                  {stageNames.map((name, i) => (
                    <span 
                      key={name} 
                      className="font-medium"
                      style={{ color: i < (user?.stage || 1) ? stageColors[i] : '#CCC' }}
                    >
                      {i + 1}
                    </span>
                  ))}
                </div>
              </div>
              
              <Link
                to="/pixel"
                className="mt-6 font-bold bg-lavender text-white px-6 py-3 rounded-2xl hover:scale-105 transition-transform shadow-lg flex items-center gap-2"
                data-testid="customise-pixel-btn"
              >
                <Palette className="w-5 h-5" />
                Customise Me!
              </Link>
            </div>
            
            {/* Stats Cards */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-mint-green/30" data-testid="accuracy-stat">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-6 h-6 text-mint-green" />
                  <span className="font-bold text-text-medium">Accuracy</span>
                </div>
                <div className="font-display text-4xl font-bold text-mint-green">
                  {stats?.average_accuracy?.toFixed(1) || '0'}%
                </div>
                <div className="text-sm text-text-medium mt-1">
                  Best: {stats?.best_accuracy?.toFixed(1) || '0'}%
                </div>
              </div>
              
              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-sunny-yellow/30" data-testid="sessions-stat">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-6 h-6 text-sunny-yellow" />
                  <span className="font-bold text-text-medium">Sessions</span>
                </div>
                <div className="font-display text-4xl font-bold text-sunny-yellow">
                  {stats?.total_sessions || 0}
                </div>
                <div className="text-sm text-text-medium mt-1">
                  {stats?.total_keystrokes?.toLocaleString() || 0} keystrokes
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="space-y-4">
              <Link
                to="/practice"
                className="block bg-gradient-to-r from-mint-green to-sky-blue text-white rounded-3xl p-6 hover:scale-105 transition-transform shadow-lg"
                data-testid="practice-btn"
              >
                <Keyboard className="w-10 h-10 mb-3" />
                <div className="font-display text-xl font-bold">Let's Type!</div>
                <div className="text-sm opacity-90">Start practicing</div>
              </Link>
              
              <Link
                to="/weekly"
                className="block bg-gradient-to-r from-sunny-yellow to-coral-orange text-white rounded-3xl p-6 hover:scale-105 transition-transform shadow-lg"
                data-testid="weekly-btn"
              >
                <Calendar className="w-10 h-10 mb-3" />
                <div className="font-display text-xl font-bold">Weekly Fun</div>
                <div className="text-sm opacity-90">+50 bonus coins!</div>
              </Link>
              
              <Link
                to="/mini-games"
                className="block bg-gradient-to-r from-lavender to-purple-500 text-white rounded-3xl p-6 hover:scale-105 transition-transform shadow-lg"
                data-testid="minigames-btn"
              >
                <Gamepad2 className="w-10 h-10 mb-3" />
                <div className="font-display text-xl font-bold">Mini Games</div>
                <div className="text-sm opacity-90">Scramble, Speed &amp; more!</div>
              </Link>
              
              <Link
                to="/badges"
                className="block bg-gradient-to-r from-amber-400 to-sunny-yellow text-white rounded-3xl p-6 hover:scale-105 transition-transform shadow-lg"
                data-testid="badges-btn"
              >
                <Star className="w-10 h-10 mb-3" />
                <div className="font-display text-xl font-bold">Badges</div>
                <div className="text-sm opacity-90">Earn achievements!</div>
              </Link>
              
              {user?.school_id && (
                <Link
                  to="/duel"
                  className="block bg-gradient-to-r from-coral-orange to-soft-pink text-white rounded-3xl p-6 hover:scale-105 transition-transform shadow-lg"
                  data-testid="duel-btn"
                >
                  <Swords className="w-10 h-10 mb-3" />
                  <div className="font-display text-xl font-bold">Friendly Duel</div>
                  <div className="text-sm opacity-90">Challenge a friend!</div>
                </Link>
              )}
              
              <Link
                to="/leaderboard"
                className="block bg-gradient-to-r from-lavender to-soft-pink text-white rounded-3xl p-6 hover:scale-105 transition-transform shadow-lg"
                data-testid="leaderboard-btn"
              >
                <Trophy className="w-10 h-10 mb-3" />
                <div className="font-display text-xl font-bold">Top Typers</div>
                <div className="text-sm opacity-90">See who's best!</div>
              </Link>
            </div>
          </div>
          
          {/* Town Visualization */}
          <div className="mt-8 bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100" data-testid="island-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-2xl font-bold text-text-dark flex items-center gap-2">
                  <Home className="w-7 h-7 text-coral-orange" />
                  Your Town
                </h2>
                <p className="text-text-medium">Watch it grow as you learn!</p>
              </div>
              <Link
                to="/island"
                className="font-bold bg-gradient-to-r from-mint-green to-sky-blue text-white px-6 py-3 rounded-2xl hover:scale-105 transition-transform shadow-lg flex items-center gap-2"
                data-testid="visit-island-btn"
              >
                <Map className="w-5 h-5" />
                Visit Town
              </Link>
            </div>
            
            {/* Simple Town Grid Visualization */}
            <div className="bg-gradient-to-b from-sky-100 to-green-100 rounded-2xl p-4">
              <div className="grid grid-cols-12 gap-1">
                {Array.from({ length: 144 }).map((_, i) => {
                  const row = Math.floor(i / 12);
                  const col = i % 12;
                  const centerRow = 6;
                  const centerCol = 6;
                  const distance = Math.sqrt(Math.pow(row - centerRow, 2) + Math.pow(col - centerCol, 2));
                  const maxDistance = Math.min(((user?.total_correct || 0) / 200) + 2, 5);
                  const isIsland = distance < maxDistance;
                  const isCore = distance < 2;
                  
                  return (
                    <div
                      key={i}
                      className={`aspect-square rounded-lg transition-all ${
                        isCore ? 'bg-sunny-yellow shadow-sm' :
                        isIsland ? 'bg-mint-green/60' :
                        'bg-sky-200/30'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
